<template>
  <main class="legal page">
    <aside class="legal-side">
      <div class="legal-side__inner">
        <sectionHeader/>

        <div class="legal-title">
          <p class="eyebrow">Quiz des métiers de la transition écologique</p>
          <h1>Mentions légales</h1>
          <p class="subtitle">Conditions générales</p>
        </div>

        <nav class="legal-toc" aria-label="Accès rapide">
          <ol>
            <li v-for="item in toc" :key="item.id">
              <a :href="'#' + item.id" @click.prevent="scrollTo(item.id)">
                <span class="label">{{ item.label }}</span>
              </a>
            </li>
          </ol>
        </nav>

        <router-link to="/" class="btn btn-back">Retour au Quiz</router-link>
      </div>
    </aside>

    <article class="legal-content">
      <p class="eyebrow">Mentions légales, conditions d'utilisation et protection des données</p>
      <p class="intro">Le présent document définit les conditions d'utilisation du Quiz des métiers de la transition écologique ainsi que les modalités relatives à la protection des données personnelles, aux droits d'auteur et aux responsabilités applicables au site Internet.</p>

      <section id="general">
        <h2>Général</h2>
        <p>En consultant des informations, indications ou documents figurant sur ce site Internet, vous déclarez adhérer aux conditions ci-après.</p>
        <p>L'État de Genève se réserve le droit de modifier, compléter ou supprimer tout ou partie du contenu du site Internet et des présentes conditions, et ce, à tout moment et selon sa libre appréciation. Il en va de même en cas d'améliorations et/ou de modifications apportées aux informations.</p>
      </section>

      <section id="a-propos">
        <h2>À propos du Quiz des métiers de la transition écologique</h2>
        <p>Le Quiz est destiné aux <b>jeunes dès 14 ans</b>, ainsi qu'au <b>personnel enseignant du cycle d'orientation et aux parents d'élèves</b> souhaitant accompagner leur enfant dans leur réflexion sur leur orientation professionnelle. Il s'adresse également aux <b>personnes en reconversion ou réorientation professionnelle</b>, aux <b>conseillers en orientation ou en emploi</b> qui les accompagnent, ainsi qu'aux <b>organismes de formation</b> qui proposent des cursus dans ce domaine.</p>
        <p>L'objectif du Quiz est d'informer les personnes en orientation professionnelle ou en recherche d'emploi sur plus d'une centaine de métiers liés à la transition écologique et de les inciter à explorer les cursus possibles pour exercer ces métiers.</p>
        <p>Il est le fruit d'une collaboration impliquant les services publics de l'énergie, de l'environnement, de l'orientation professionnelle et de l'enseignement obligatoire du canton de Genève.</p>
        <p>Il s'inscrit également dans un partenariat transfrontalier dans le cadre du programme Interreg VI nommé PLA.N.E.T.TE (Plateforme numérique de l'employabilité transfrontalière et de la transition écologique), les fiches métiers dans leurs dénominations françaises relevant de la responsabilité des partenaires français.</p>
        <a class="link-card" href="https://www.ge.ch/document/donner-du-sens-son-travail-engageant-dans-metier-transition-ecologique" target="_blank" rel="noopener">
          <span>
            <small>Plus d'informations · ge.ch</small>
            Donner du sens à son travail en s'engageant dans un métier de la transition écologique
          </span>
          <span class="arrow" aria-hidden="true">↗</span>
        </a>
      </section>

      <section id="partenaires">
        <h2>Partenaires</h2>
        <dl class="partners">
          <div>
            <dt>Initiation du projet</dt>
            <dd>État de Genève (OCEN) et MBG.</dd>
          </div>
          <div>
            <dt>Comité de pilotage</dt>
            <dd>OCEN, OFPC, DGEO, SESAC.</dd>
          </div>
          <div>
            <dt>Réalisation</dt>
            <dd>
              <ul>
                <li><b><a href="pierre52.clp@gmail.com" target="_blank">CLP – Cité Lumière Production</a></b> – Genève – Pierre Olivier (conception, réalisation, rédaction et iconographie, coordination),</li>
                <li><b><a href="https://polomarco.ch/" target="_blank">Polomarco</a></b> – Genève (conseil, design et production du site Internet),</li>
                <li><b><a href="https://bart-studio.ch/" target="_blank">Bart Studio</a></b> – Genève (développement web),</li>
                <li><b><a href="https://www.bertrandcarlier.com/" target="_blank">Bertrand Carlier</a></b> (photographies originales).</li>
              </ul>
            </dd>
          </div>
          <div>
            <dt>Financement</dt>
            <dd>
              <ul>
                <li>État de Genève (OCEN, DDC, OCEau, OCAN, OCEV, OCT, SAERF),</li>
                <li>Fondation pour la formation professionnelle et continue du canton de Genève (FFPC),</li>
                <li>Confédération suisse (OFEN, SECO).</li>
              </ul>
            </dd>
          </div>
          <div>
            <dt>Pôle métropolitain du Genevois français</dt>
            <dd>Adaptation des fiches métiers au contexte français.</dd>
          </div>
          <div>
            <dt>Développement transfrontalier</dt>
            <dd>
              <ul>
                <li>État de Genève (OFPC – Cité des métiers du Grand Genève),</li>
                <li>Pôle métropolitain du Genevois français et Centres associés français de la Cité des métiers du Grand Genève (Annemasse, Terres Valserhône, Bonneville, Thonon).</li>
              </ul>
            </dd>
          </div>
        </dl>
      </section>

      <section id="donnees">
        <h2>Protection des données</h2>
        <p>La Constitution de la République et du canton de Genève ainsi que la législation genevoise sur la protection des données disposent que toute personne a droit à la protection de sa sphère privée, ainsi qu'à la protection contre l'emploi abusif des données personnelles et sensibles qui la concernent. L'État de Genève observe strictement ces dispositions et prend toutes les mesures nécessaires afin de respecter les différentes lois en matière de protection des données personnelles.</p>
        <p>Les données suivantes sont collectées et traitées :</p>
        <ul class="bullets">
          <li>adresse de courrier électronique ;</li>
          <li>adresse IP ;</li>
          <li>données d'utilisation (pages consultées, durée de consultation) ;</li>
          <li>réponses aux questions du Quiz.</li>
        </ul>
        <p>L'adresse de courrier électronique sert uniquement à l'enregistrement et transmission des résultats du Quiz à l'utilisateur ou l'utilisatrice. Elle n'est en aucun cas utilisée à d'autres fins ou transmise à des tiers. Son renseignement est facultatif.</p>
        <p>L'utilisateur ou l'utilisatrice dispose des droits en lien avec la protection de ses données personnelles, notamment le droit d'information et le droit d'opposition concernant les données personnelles.</p>
        <p>Les informations et données personnelles ne sont utilisées qu'à des fins statistiques anonymisées à l'usage de l'État de Genève et du Pôle Métropolitain du Genevois français.</p>
        <p>Pour faire valoir son droit à l'effacement et pour toute question relative aux données personnelles : voir chapitre « Contact ».</p>
      </section>

      <section id="droits-auteur">
        <h2>Droits d'auteur</h2>
        <p>Le droit d'auteur est régi par la Constitution fédérale de la Confédération suisse et la loi fédérale sur le droit d'auteur.</p>
        <p>L'État de Genève est propriétaire des droits d'auteur du Quiz des métiers de la transition écologique, lesquels lui ont été cédés par les mandataires en charge de la réalisation.</p>
        <p>L'usage des photographies originales est autorisé aux conditions suivantes :</p>
        <ul class="bullets">
          <li>Utilisation dans le cadre de la promotion du Quiz (usage Web, plaquettes, affiches).</li>
          <li>Indication de la mention « © Bertrand Carlier ».</li>
          <li>Citation des entreprises participantes sous la mention « [nom de l'entreprise] © Bertrand Carlier ».</li>
        </ul>
        <p>Toute utilisation à d'autres fins (publication presse, livres, etc.) ne peut se faire qu'avec l'accord explicite du photographe et ouvre des droits à rémunération.</p>
        <p>Le nom, l'ensemble du contenu (textes, graphiques, photos, charte graphique, etc.), l'algorithme de calcul des résultats ainsi que le code source du site Internet sont protégés par un droit d'auteur et, sauf disposition contraire, sont la propriété exclusive et totale de l'État de Genève. Le site Internet est accessible publiquement. Néanmoins le téléchargement ou la copie de textes, d'illustrations, de photographies, de vidéos ou de toute autre donnée n'entraîne aucun transfert de droits sur les contenus, ni aucun autre droit tel que notamment une retransmission.</p>
        <p>L'utilisateur ou l'utilisatrice doit respecter les indications relatives à des droits de protection et d'utilisation de tiers susceptibles de figurer sur ce site Internet.</p>
        <p>L'État de Genève fournit les informations contenues sur le site Internet à des fins exclusives d'usage privé et d'information.</p>
        <p>Il est permis de télécharger, d'enregistrer ou d'imprimer certaines pages et/ou parties du site, à condition de ne supprimer ni des mentions de droits d'auteur, ni d'autres désignations légalement protégées.</p>
        <p>Toute reproduction (partielle ou totale) ou utilisation à des fins publiques ou commerciales est subordonnée à l'autorisation écrite préalable de l'État de Genève, respectivement des titulaires des droits.</p>
      </section>

      <section id="garantie">
        <h2>Exclusion de garantie</h2>
        <p>Bien que l'État de Genève s'emploie en toute bonne foi à garantir la fiabilité des informations contenues dans le site au moment de leur publication, il ne peut certifier ou attester formellement de manière explicite ou implicite (également vis-à-vis de tiers) leur exactitude, leur fiabilité ou leur exhaustivité.</p>
        <p>Les opinions émises ou autres informations contenues sur le site Internet peuvent être modifiées en tout temps sans avertissement préalable. L'État de Genève se dégage de toute responsabilité et ne garantit nullement que les fonctions disponibles sur le site Internet ne soient jamais interrompues ni l'absence de virus ou autres éléments dommageables dans le navigateur concerné.</p>
      </section>

      <section id="responsabilite">
        <h2>Restriction de la responsabilité</h2>
        <p>L'État de Genève se dégage de toute responsabilité (également en cas de faute) pour les dommages directs, indirects ou consécutifs pouvant résulter de l'accès aux éléments du site Internet ou de leur utilisation, respectivement de l'impossibilité d'y accéder ou de les utiliser ou encore résultant de la connexion à d'autres sites Internet.</p>
      </section>

      <section id="liens">
        <h2>Liens vers d'autres sites Internet</h2>
        <p>Le site Internet contient des liens vers des sites tiers. L'activation de ces liens peut impliquer un changement de site Internet ou l'apparition de fenêtres de sites tiers dans l'environnement du site Internet. L'État de Genève n'a aucun contrôle sur les sites Internet tiers et ne peut être en aucun cas tenu responsable du contenu et du fonctionnement de ces sites Internet. Les risques pouvant découler de la connexion avec des sites tiers ou de leur consultation relèvent exclusivement de la responsabilité de l'utilisateur ou de l'utilisatrice.</p>
      </section>

      <section id="hebergement">
        <h2>Hébergement et développement</h2>
        <p>Le site Internet est hébergé en Suisse sur Infomaniak et développé à l'aide du Content Management System WordPress.</p>
        <p>Les URL permettant d'accéder au Quiz des métiers de la transition écologique sont les suivantes :</p>
        <ul class="bullets">
          <li><a href="https://quiz-metiers-transition-energetique.ch/" target="_blank" rel="noopener">https://quiz-metiers-transition-energetique.ch/</a></li>
          <li><a href="https://quiz-metiers-transition-ecologique.ch/" target="_blank" rel="noopener">https://quiz-metiers-transition-ecologique.ch/</a></li>
        </ul>
        <p>Les statistiques de fréquentation sont consultées exclusivement via les outils statistiques mis à disposition par Infomaniak. Ces données sont traitées conformément aux prescriptions applicables en matière de protection des données et sont utilisées uniquement afin d'analyser la fréquentation du site et d'en améliorer le fonctionnement.</p>

        <h3>Conditions d'utilisation et protection des données</h3>
        <ul class="terms">
          <li><b>Confidentialité :</b> les informations statistiques sont traitées avec confidentialité, non vendues et non divulguées, sauf sur ordonnance judiciaire.</li>
          <li><b>Conformité RGPD :</b> Infomaniak est en pleine conformité avec les prescriptions applicables en matière de protection des données, assurant sécurité, transparence et contrôle.</li>
          <li><b>Usage des données :</b> les données ne sont pas utilisées pour le compte de tiers.</li>
          <li><b>Statistiques site Web :</b> elles permettent d'analyser la fréquentation, les pays et le type de technologies, avec possibilité d'exporter les données.</li>
        </ul>
        <p>Le site utilise les cookies strictement nécessaires à son bon fonctionnement ainsi que les outils statistiques proposés par Infomaniak afin d'analyser sa fréquentation et d'améliorer l'expérience utilisateur, conformément à la législation applicable.</p>
      </section>

      <section id="contact" class="contact">
        <div class="contact-card">
          <h2>Contact</h2>
          <div class="contact-main">
            <address>
              <h3>Office cantonal de l'énergie</h3>
              <p>Rue du Puits-St-Pierre 4</p>
              <p>1204 Genève</p>
              <a href="mailto:ocen@etat.ge.ch">ocen@etat.ge.ch</a>
            </address>
          </div>
          <p class="contact-note">Droit à l'effacement et questions relatives aux données personnelles : contactez l'Office cantonal de l'énergie à l'adresse ci-dessus.</p>
        </div>
      </section>
    </article>
  </main>
</template>

<script>
import sectionHeader from '@/components/sectionHeader.vue'

export default {
  name: 'MentionsLegales',
  components: {
    sectionHeader
  },
  data () {
    return {
      toc: [
        { id: 'a-propos', label: 'À propos du Quiz' },
        { id: 'partenaires', label: 'Partenaires' },
        { id: 'donnees', label: 'Protection des données' },
        { id: 'droits-auteur', label: 'Droits d\'auteur' },
        { id: 'contact', label: 'Contact' }
      ]
    }
  },
  methods: {
    scrollTo (id) {
      const el = document.getElementById(id)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }
}
</script>

<style lang="scss">
  $green: #59F87F;
  $border: #000;

  main.legal {
    grid-template-columns: minmax(300px, 5fr) 7fr;
    align-items: start;

    @media all and (max-width: 768px) {
      display: block;
    }

    .eyebrow {
      margin: 0 0 20px;
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 0.1em;
      text-transform: uppercase;
    }

    // ---------- Colonne gauche (sommaire) ----------
    .legal-side {
      position: sticky;
      top: 0;
      height: 100vh;
      box-sizing: border-box;
      border-right: 1px solid $border;
      overflow-y: auto;

      @media all and (max-width: 768px) {
        position: static;
        height: auto;
        border-right: 0;
      }
    }

    .legal-side__inner {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      text-align: right;
      min-height: 100%;
      box-sizing: border-box;
      padding: 0 40px 40px 70px;

      @media all and (max-width: 768px) {
        padding: 0 20px 20px;
      }

      .section-header {
        margin-bottom: 8vh;
        margin-left: 0;

        .quiz_title {
          margin-left: -70px;

          @media all and (max-width: 768px) {
            margin-left: -20px;
          }
        }

        @media all and (max-width: 768px) {
          margin-bottom: 30px;
        }
      }
    }

    .legal-title {
      margin-bottom: 60px;

      h1 {
        font-family: 'Fredoka One', cursive;
        font-weight: normal;
        font-size: 64px;
        line-height: 1;
        margin: 0;

        @media all and (max-width: 1100px) {
          font-size: 48px;
        }
        @media all and (max-width: 540px) {
          font-size: 40px;
        }
      }

      .subtitle {
        margin: 14px 0 0;
        font-size: 22px;
        text-transform: uppercase;
      }
    }

    .legal-toc {
      width: 100%;
      margin-bottom: 40px;

      ol {
        list-style: none;
        margin: 0;
        padding: 0;
        border-top: 1px solid $border;
      }

      li {
        border-bottom: 1px solid $border;
      }

      a {
        display: flex;
        justify-content: flex-end;
        align-items: center;
        gap: 16px;
        padding: 10px 0;
        color: #000;
        text-decoration: none;
        transition: background-color 0.2s;

        &:hover {
          text-decoration: underline;
          text-underline-offset: 4px;
        }
      }

    }

    .btn-back {
      font-size: 18px;
      padding: 5px 25px;
    }

    // ---------- Colonne droite (contenu) ----------
    .legal-content {
      padding: 120px 70px 80px 70px;
      text-align: left;
      max-width: 900px;

      @media all and (max-width: 768px) {
        padding: 30px 20px 40px;
      }

      .intro {
        font-size: 22px;
        line-height: 1.4;
        margin: 0 0 60px;

        @media all and (max-width: 540px) {
          font-size: 18px;
        }
      }

      section {
        border-top: 1px solid $border;
        padding: 45px 0 55px;
        scroll-margin-top: 0;
      }

      h2 {
        display: flex;
        align-items: baseline;
        gap: 14px;
        font-family: 'Fredoka One', cursive;
        font-weight: normal;
        font-size: 38px;
        line-height: 1.1;
        margin: 0 0 30px;

        @media all and (max-width: 540px) {
          font-size: 28px;
        }
      }

      h3 {
        font-family: 'Fredoka One', cursive;
        font-weight: normal;
        font-size: 22px;
        margin: 35px 0 15px;
      }

      p, li, dd, address {
        font-size: 16px;
        line-height: 1.65;
        font-style: normal;
      }

      p {
        margin: 0 0 16px;
      }

      b {
        font-weight: 600;
      }

      a {
        color: #000;
        text-underline-offset: 3px;

        &:hover {
          background-color: $green;
        }
      }

      ul.bullets,
      ul.terms {
        margin: 0 0 16px;
        padding-left: 20px;
      }

      ul.terms li {
        margin-bottom: 8px;
      }

      // Bloc contact
      section.contact {
        border-top: 0;
        padding: 45px 0 0;
      }

      .contact-card {
        background-color: #000;
        color: #fff;
        border-radius: 40px;
        padding: 45px 55px 40px;

        @media all and (max-width: 540px) {
          border-radius: 28px;
          padding: 30px 24px;
        }

        h2 {
          margin-bottom: 30px;

        }

        h3 {
          font-size: 26px;
          margin: 0 0 18px;
        }

        address p {
          margin: 0 0 6px;
        }

        a {
          color: #fff;

          &:hover {
            background-color: transparent;
            color: $green;
          }
        }

        address > a {
          display: inline-block;
          margin-top: 10px;
          font-weight: 600;
        }

        .contact-main {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          flex-wrap: wrap;
          gap: 24px;
          padding-bottom: 30px;
          border-bottom: 1px solid #fff;
        }


        .contact-note {
          margin: 22px 0 0;
          font-size: 14px;
          line-height: 1.6;
        }
      }

      // Carte de lien externe
      .link-card {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 20px;
        margin-top: 30px;
        padding: 16px 22px;
        border: 3px solid #000;
        border-radius: 20px;
        text-decoration: none;
        transition: all 0.25s;

        small {
          display: block;
          margin-bottom: 4px;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
        }

        .arrow {
          font-size: 20px;
        }

        &:hover {
          background-color: #000;
          color: #fff;
        }
      }

      // Tableau des partenaires
      .partners {
        margin: 0;
        border-top: 1px solid $border;

        & > div {
          display: grid;
          grid-template-columns: 1fr 2fr;
          gap: 20px;
          padding: 16px 0;
          border-bottom: 1px solid $border;

          @media all and (max-width: 540px) {
            grid-template-columns: 1fr;
            gap: 6px;
          }
        }

        dt {
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          line-height: 1.8;
        }

        dd {
          margin: 0;

          ul {
            margin: 0;
            padding: 0;
            list-style: none;
          }

          li + li {
            margin-top: 6px;
          }
        }
      }
    }
  }
</style>
